import { api } from "@/lib/api/client";
import type { components } from "@/types/api";
import type { StepUpProof } from "@/features/account/api/stepup.api";

export type StaffRole = components["schemas"]["StaffMembership"]["role"];

export const SUPPORT_CONTEXT_STORAGE_KEY = "aether_support_context";

export function clearStoredSupportContext(): void {
  try {
    sessionStorage.removeItem(SUPPORT_CONTEXT_STORAGE_KEY);
  } catch {
    return;
  }
}

export interface SupportContext {
  id: string;
  targetAccountId: string;
  reason: string;
  openedAt: string;
  expiresAt: string;
}

export interface SupportAccountMatch {
  accountId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  deletionScheduledFor: string | null;
  createdAt: string;
}

export interface SupportTenant {
  tenantId: string;
  planTier: string;
  billingStatus: string | null;
  billingCycleAnchor: string | null;
  planUpdatedAt: string | null;
  hasPaymentMethod: boolean;
  madQuota: number;
  retentionDays: number;
  maxApps: number;
  maxDeploymentsPerApp: number;
  maxApiKeys: number;
  apiRateLimitPerMinute: number;
}

export interface SupportTransfer {
  id: string;
  appId: string;
  appName: string;
  fromEmail: string;
  toEmail: string;
  status: string;
  createdAt: string;
  expiresAt: string;
}

export interface SupportApp {
  appId: string;
  name: string;
  tenantId: string;
  createdAt: string;
  role: string | null;
  collaboratorCount: number;
}

export interface SupportDeployment {
  deploymentId: string;
  appId: string;
  name: string;
  keyPrefix: string | null;
  createdAt: string;
  latestLabel: string | null;
  latestReleasedAt: string | null;
  isDisabled: boolean | null;
}

export function searchAccounts(email: string): Promise<{ accounts: SupportAccountMatch[] }> {
  return api.post<{ accounts: SupportAccountMatch[] }>(
    "/v1/staff/accounts/search",
    { email },
    { retrySafe: true },
  );
}

export function openSupportContext(body: {
  targetAccountId: string;
  reason: string;
  stepUp?: StepUpProof;
}): Promise<{ context: SupportContext }> {
  return api.post<{ context: SupportContext }>("/v1/staff/contexts", body);
}

export function closeSupportContext(contextId: string): Promise<void> {
  return api.delete<void>(`/v1/staff/contexts/${encodeURIComponent(contextId)}`);
}

export function getSupportAccount(
  accountId: string,
  contextId: string,
): Promise<{
  account: SupportAccountMatch;
  tenant: SupportTenant | null;
  transfers: SupportTransfer[];
}> {
  return api.get(
    `/v1/staff/accounts/${encodeURIComponent(accountId)}?contextId=${encodeURIComponent(contextId)}`,
  );
}

export function getSupportApps(
  accountId: string,
  contextId: string,
): Promise<{ apps: SupportApp[] }> {
  return api.get(
    `/v1/staff/accounts/${encodeURIComponent(accountId)}/apps?contextId=${encodeURIComponent(contextId)}`,
  );
}

export function getSupportDeployments(
  appId: string,
  contextId: string,
): Promise<{ app: SupportApp; deployments: SupportDeployment[] }> {
  return api.get(
    `/v1/staff/apps/${encodeURIComponent(appId)}/deployments?contextId=${encodeURIComponent(contextId)}`,
  );
}
